import { OTLayout } from "@/components/layout/ot-layout";
import { Award, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useState } from "react";

const FELLOWSHIP_CATEGORIES = [
  {
    title: "Clinical Practice",
    description: "For OTs who have made an exceptional contribution to occupational therapy clinical practice, demonstrating sustained expertise at a national or international level in a defined clinical area.",
    evidence: [
      "National or international recognition as a clinical expert in rheumatology OT",
      "Development of new clinical approaches or interventions with demonstrable impact",
      "Invited speaker at national conferences (RCOT Annual, BSR Annual)",
      "Clinical consultancy across organisations or regions",
      "Publications in peer-reviewed journals on clinical practice",
    ],
  },
  {
    title: "Education",
    description: "For OTs who have made an outstanding contribution to occupational therapy education, advancing the knowledge, skills, and development of the OT workforce.",
    evidence: [
      "Sustained leadership of OT educational programmes at HEI level",
      "Development of innovative educational resources or curricula used nationally",
      "Supervision of multiple OT postgraduate students (MSc, PhD)",
      "National influence on OT education policy or standards",
      "RCOT or specialist section educational leadership roles",
    ],
  },
  {
    title: "Research",
    description: "For OTs who have made a significant contribution to occupational therapy or occupational science research, generating evidence that has changed or informed OT practice.",
    evidence: [
      "Principal or co-investigator on funded rheumatology OT research",
      "Substantive publication record in peer-reviewed OT or rheumatology journals",
      "Research that has influenced national clinical guidelines or policy",
      "External grant funding (NIHR, Arthritis Research UK, RCOT research fund)",
      "Contribution to systematic reviews or Cochrane collaboration",
    ],
  },
  {
    title: "Leadership & Service Development",
    description: "For OTs who have made an exceptional contribution to OT leadership, management, or service development, resulting in measurable improvement in services or outcomes.",
    evidence: [
      "National or international OT leadership role (RCOT board, WFOT, strategic NHS role)",
      "Service transformation with demonstrable patient outcome impact at scale",
      "Development of new rheumatology OT services or models of care",
      "Policy contribution at national level (NICE, NHS England, DHSC)",
      "Sustained contribution to RCOT SS Rheumatology or BSR OT SIG at national level",
    ],
  },
];

const APPLICATION_STEPS = [
  { step: "1", title: "Check eligibility", detail: "You must be a current full RCOT member. There is no minimum years of experience requirement, but fellowship is for OTs who have made sustained, exceptional contributions to the profession." },
  { step: "2", title: "Choose your category", detail: "Select the category (Clinical Practice, Education, Research, or Leadership) that best reflects your contribution. You can apply in more than one category." },
  { step: "3", title: "Prepare your application", detail: "Compile evidence of your contribution including publications, conference presentations, service reports, testimonials, and details of national roles. Application forms are available from RCOT." },
  { step: "4", title: "Obtain nominations", detail: "You will need nominations from two RCOT members (ideally Fellows or senior colleagues) who can attest to the significance and impact of your contribution." },
  { step: "5", title: "Submit to RCOT", detail: "Applications are reviewed by the RCOT Fellowship Committee. The process typically opens annually. Check the RCOT website for current application windows and guidance." },
  { step: "6", title: "Award ceremony", detail: "Successful applicants are awarded FRCOT status at the RCOT Annual Conference. Fellows are listed in RCOT publications and may use the post-nominal 'FRCOT'." },
];

const FAQ = [
  { q: "How long does it take to achieve FRCOT?", a: "There is no fixed timescale. Fellowship recognises the totality of your career contribution, not simply years of experience. Some OTs apply after 10 years; others after 25 or more. What matters is the quality and significance of the contribution." },
  { q: "Do I need to be a Consultant OT to apply?", a: "No. Band is not a criterion for FRCOT. Fellowship is awarded on the basis of contribution and impact, not NHS banding. However, most Fellows will have made national-level contributions that are more typical of senior or consultant-level roles." },
  { q: "Can I apply if I work outside the NHS?", a: "Yes. RCOT Fellowship is open to all RCOT members regardless of sector — NHS, independent, third sector, academia, or private practice." },
  { q: "Is FRCOT recognised outside the UK?", a: "FRCOT is a UK professional recognition awarded by RCOT. It is well-regarded within UK rheumatology and occupational therapy circles. International recognition depends on the country and professional context." },
];

export default function OtFrcot() {
  const [openCat, setOpenCat] = useState<number | null>(null);
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <OTLayout title="RCOT Fellowship (FRCOT) Guide">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-rose-700 to-rose-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Award className="h-7 w-7 text-rose-300" />
            <div>
              <h2 className="text-xl font-bold">RCOT Fellowship (FRCOT)</h2>
              <p className="text-rose-200 text-sm">The highest professional honour awarded by RCOT</p>
            </div>
          </div>
          <p className="text-rose-100 text-sm leading-relaxed">
            RCOT Fellowship (FRCOT) recognises occupational therapists who have made an outstanding and sustained contribution to the OT profession — in clinical practice, education, research, or leadership. This guide explains the categories, application process, and how to prepare a strong fellowship application as a rheumatology OT.
          </p>
          <a
            href="https://www.rcot.co.uk/membership/fellowship"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-rose-200 hover:text-white font-medium"
          >
            <ExternalLink className="h-4 w-4" />
            Visit official RCOT Fellowship pages
          </a>
        </div>

        {/* What is FRCOT */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">What is RCOT Fellowship?</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            RCOT Fellowship (FRCOT) is the highest professional distinction awarded by the Royal College of Occupational Therapists. It is conferred on members who have made a sustained, significant, and nationally or internationally recognised contribution to occupational therapy.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            Fellowship is not an examination or qualification — it is a recognition of career achievement. Recipients are entitled to use the post-nominal letters <strong>FRCOT</strong> and are listed in RCOT's register of Fellows.
          </p>
        </div>

        {/* Fellowship Categories */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h3 className="font-bold text-slate-900">Fellowship Categories</h3>
            <p className="text-xs text-slate-500 mt-1">Select the category that best reflects your primary contribution to occupational therapy.</p>
          </div>
          <div className="divide-y divide-slate-100">
            {FELLOWSHIP_CATEGORIES.map((cat, i) => (
              <div key={i}>
                <button
                  onClick={() => setOpenCat(openCat === i ? null : i)}
                  className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{cat.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{cat.description}</p>
                  </div>
                  {openCat === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0 ml-4" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 ml-4" />}
                </button>
                {openCat === i && (
                  <div className="px-6 pb-5 space-y-3">
                    <p className="text-sm text-slate-600 leading-relaxed">{cat.description}</p>
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-2">Typical evidence for rheumatology OTs:</p>
                      <ul className="space-y-1.5">
                        {cat.evidence.map((e, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-400 shrink-0" />
                            {e}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Application Process */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Application Process</h3>
          <div className="space-y-3">
            {APPLICATION_STEPS.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenStep(openStep === i ? null : i)}
                  className="flex items-center gap-4 w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="flex-shrink-0 h-7 w-7 bg-rose-700 text-white rounded-full text-xs font-bold flex items-center justify-center">{s.step}</span>
                  <span className="text-sm font-medium text-slate-800 flex-1">{s.title}</span>
                  {openStep === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                </button>
                {openStep === i && (
                  <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {s.detail}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Frequently Asked Questions</h3>
          <div className="space-y-2">
            {FAQ.map((faq, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-medium text-slate-800">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0 ml-4" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 ml-4" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Information source:</strong> Based on publicly available RCOT Fellowship guidance. Application criteria, timelines, and processes may change. Always check the official RCOT website (rcot.co.uk/membership/fellowship) for the most current information before applying.
        </div>
      </div>
    </OTLayout>
  );
}
